/**
 * The WebSocket event envelope (PLAN §10.1, §6.5): one contract for the local
 * state service and the cloud API Gateway alike.
 *
 *     { type, tenantId, rundownId?, payload, ts }
 *
 * `type` discriminates the payload. Subscriptions are per rundown (§6.5), so
 * every rundown-scoped message requires `rundownId`; `ping`/`pong` carry none.
 *
 *   client → server   subscribe, unsubscribe, row.patch, presence.update,
 *                     playout.subscribe, playout.unsubscribe, changes.subscribe,
 *                     changes.unsubscribe, ping
 *   server → client   subscribed, row.patch (another writer's batch), row.state,
 *                     presence.state, presence.leave, playout.state, device.state, playout.link,
 *                     changes.subscribed, resource.changed, nack, pong
 *
 * The playout messages are per PRODUCTION (`productionId` instead of
 * `rundownId`, PLAN §6.6): see the section below.
 *
 * **The catch-up handshake.** A client (re)connecting sends `subscribe`, waits
 * for `subscribed`, and only then pulls `GET /rundowns/{id}/rows?changedSince=`
 * from its last cursor. Subscribing first means nothing written during the
 * pull is missed; a row arriving by both paths merges twice, harmlessly.
 *
 * `tenantId` on an inbound message is a routing hint only. The tenant a server
 * acts under comes from the verified token and nowhere else (D13); a handler
 * must reject a message whose `tenantId` disagrees with it.
 */
import { epochMsSchema, fieldPathSchema, opaqueIdSchema, ulidSchema } from '@acp/domain/primitives.js';
import { patchRejectionSchema } from '@acp/domain/rejections.js';
import { rowPatchSchema, rowSchema } from '@acp/domain/row.js';
import { z } from 'zod';
import { errorCodeSchema } from './errors.js';
import { deviceStatusSchema, playoutStateChangeSchema } from './playout.js';
import { cursorSchema, MAX_PATCHES_PER_BATCH } from './rows.js';
import { resourceChangeSchema } from './changes.js';
import { type ContractParsing, parseContract } from './parsing.js';

const tenantIdSchema = z.string().min(1).max(128);
/** Milliseconds since the epoch, stamped by the sender. Informational — ordering comes from HLCs. */
const tsSchema = z.number().int().nonnegative();

function rundownMessage<T extends string, P extends z.ZodType>(type: T, payload: P) {
  return z.object({ type: z.literal(type), tenantId: tenantIdSchema, rundownId: ulidSchema, payload, ts: tsSchema }).strict();
}

function bareMessage<T extends string>(type: T) {
  return z.object({ type: z.literal(type), tenantId: tenantIdSchema, payload: z.object({}).strict(), ts: tsSchema }).strict();
}

/** Start receiving a rundown's `row.state` and `row.patch` events. Needs `rundown.read`. */
export const subscribeMessageSchema = rundownMessage('subscribe', z.object({}).strict());

/**
 * The subscription is live. `cursor` (opaque, `cursorSchema`) is the
 * rundown's high-water mark at that moment: a catch-up `changedSince` from the client's last cursor
 * covers everything up to it, and the socket covers everything after.
 */
export const subscribedMessageSchema = rundownMessage('subscribed', z.object({ cursor: cursorSchema }).strict());

export const unsubscribeMessageSchema = rundownMessage('unsubscribe', z.object({}).strict());

/**
 * A patch batch (PLAN §6.2: a block drag is one multi-row batch sharing an
 * HLC). Client → server: to merge; needs `rundown.edit`. Server → client:
 * another writer's batch as merged, for optimistic views; the canonical state
 * follows as `row.state`.
 */
export const rowPatchMessageSchema = rundownMessage(
  'row.patch',
  z.object({ patches: z.array(rowPatchSchema).min(1).max(MAX_PATCHES_PER_BATCH) }).strict(),
);

/**
 * The canonical row after a merge (PLAN §6.5). Sent to every subscriber; to
 * the patch's origin it carries the `clientOpId` it answers (its ack), to
 * everyone else `clientOpId` is null.
 */
export const rowStateMessageSchema = rundownMessage('row.state', z.object({ row: rowSchema, clientOpId: opaqueIdSchema.nullable() }).strict());

/**
 * A refusal: of one patch (`clientOpId`/`rowId` set, `rejected` lists the
 * reasons, `row` is the canonical state to roll back to — null if the row does
 * not exist), or of a whole message (a bad frame, a forbidden subscribe:
 * `clientOpId` and `rowId` null, `rejected` empty). `rundownId` is absent when
 * the frame was too broken to name one.
 */
export const nackMessageSchema = z
  .object({
    type: z.literal('nack'),
    tenantId: tenantIdSchema,
    rundownId: ulidSchema.optional(),
    /** Set on the refusal of a playout message (`playout.subscribe`). */
    productionId: ulidSchema.optional(),
    payload: z
      .object({
        error: errorCodeSchema,
        message: z.string(),
        clientOpId: opaqueIdSchema.nullable(),
        rowId: ulidSchema.nullable(),
        rejected: z.array(patchRejectionSchema),
        row: rowSchema.nullable(),
      })
      .strict(),
    ts: tsSchema,
  })
  .strict();

// ─── presence (PLAN §6.6: ephemeral, "latest wins" per connection) ───
//
// Who is looking at a rundown, and what they are editing. Never stored as
// rows, never replicated, never audited: a connection announces its state
// with `presence.update` — on joining, on every change, and at least every
// PRESENCE_REFRESH_MS — and a server forgets it PRESENCE_TTL_MS after the last
// one, or at once on `unsubscribe` or disconnect.
//
// A server answers every `presence.update` with the rundown's `presence.state`
// (the full member list), and sends the same frame to every other subscriber
// whenever the list CHANGED (a join, a state change, a leave, an expiry) — a
// refresh that changes nothing is answered to its sender only. A departure is
// announced as `presence.leave` just before the `presence.state` without it.
// `subscribe` is answered with the current `presence.state` after
// `subscribed`. A client never expires members on its own: the server is the
// authority, and its next `presence.state` replaces the list wholesale. Frames
// can overtake each other in the cloud (fan-out from concurrent Lambdas), so
// a client applies a `presence.state` only if its `ts` is not older than the
// last one it applied.

/** A member not refreshed for this long is dropped by the server. */
export const PRESENCE_TTL_MS = 30_000;
/** How often a client re-announces an unchanged state, so it outlives PRESENCE_TTL_MS with two misses to spare. */
export const PRESENCE_REFRESH_MS = 10_000;
/** The most members one `presence.state` lists (the frame stays far under 128 KB). */
export const MAX_PRESENCE_MEMBERS = 200;

export const presenceActivitySchema = z.enum(['viewing', 'editing']);

/** Where a member is: the row (null: the rundown as a whole) and field (null: the row as a whole) they are on. */
export const presenceStateSchema = z
  .object({ rowId: ulidSchema.nullable(), field: fieldPathSchema.nullable(), activity: presenceActivitySchema })
  .strict();

/**
 * One connection's presence. A user with two tabs is two members (same
 * `userId`, different `connectionId`). `displayName` is the user's email in
 * the cloud, the token's email (else the user id) on a node. `updatedAt` is
 * when the STATE last changed (ms, server clock), not the last refresh.
 */
export const presenceMemberSchema = z
  .object({
    userId: z.string().min(1).max(128),
    displayName: z.string().min(1).max(320),
    connectionId: z.string().min(1).max(128),
    state: presenceStateSchema,
    updatedAt: epochMsSchema,
  })
  .strict();

/** Client → server: this connection's state on the rundown. Needs `rundown.read`. */
export const presenceUpdateMessageSchema = rundownMessage('presence.update', z.object({ state: presenceStateSchema }).strict());

/** Server → client: every live member of the rundown, sorted by `displayName` then `connectionId`. */
export const presenceStateMessageSchema = rundownMessage(
  'presence.state',
  z.object({ members: z.array(presenceMemberSchema).max(MAX_PRESENCE_MEMBERS) }).strict(),
);

/** Server → client: a member left (unsubscribed, disconnected, or expired). */
export const presenceLeaveMessageSchema = rundownMessage(
  'presence.leave',
  z.object({ connectionId: z.string().min(1).max(128), userId: z.string().min(1).max(128) }).strict(),
);

// ─── playout (PLAN §6.6: the engine-state stream's snapshots, for UIs) ───
//
// `playout.subscribe` (needs `rundown.read` on the production's studio) is
// answered with a `playout.state` of kind `full` — the state now, with its
// `seq` — and from then on the subscriber hears every change as a
// `playout.state` (a `delta` from the previous `seq`, or a `full` one) and
// every device-status change as `device.state`. A delta whose `fromSeq` is not
// the client's `seq` means it missed a frame: it re-reads
// `GET /productions/{id}/playout`. Refused with a `nack` naming the
// production: `not-found`, `forbidden`, or `unavailable` when the production
// process cannot be reached (in the cloud always, until phase 4).
//
// `playout.link` says whether the node still hears the production process
// (PLAN §4.1: it is a process of its own, and can die while the node and this
// socket are fine): `down` when an attached process's link is lost — the
// state and devices held are stale until it is back — and `up` once it is
// attached again, after the `full` state it answers with. A client that
// never saw a `down` can ignore an `up`.

function productionMessage<T extends string, P extends z.ZodType>(type: T, payload: P) {
  return z.object({ type: z.literal(type), tenantId: tenantIdSchema, productionId: ulidSchema, payload, ts: tsSchema }).strict();
}

export const playoutSubscribeMessageSchema = productionMessage('playout.subscribe', z.object({}).strict());
export const playoutUnsubscribeMessageSchema = productionMessage('playout.unsubscribe', z.object({}).strict());
export const playoutStateMessageSchema = productionMessage('playout.state', playoutStateChangeSchema);
export const deviceStateMessageSchema = productionMessage('device.state', z.object({ devices: z.array(deviceStatusSchema) }).strict());
/** `message`: why, for the operator; null when there is nothing to add. */
export const playoutLinkMessageSchema = productionMessage(
  'playout.link',
  z.object({ status: z.enum(['up', 'down']), message: z.string().max(1000).nullable() }).strict(),
);

// ─── the tenant change channel (D35, `changes.ts`) ───
//
// `changes.subscribe` (any signed-in member of the tenant: the frames carry
// ids only, and every read they prompt is checked over REST) is answered with
// `changes.subscribed`; from then on the connection hears a `resource.changed`
// for every write to one of its tenant's resources, by anyone, through any
// path — its own writes included (the SPA needs no special case, a refetch of
// what it just saved is cheap). `changes.unsubscribe` stops them (no reply).
// A connection that drops misses what changed meanwhile: a client refetches
// what it shows when it re-subscribes.

export const changesSubscribeMessageSchema = bareMessage('changes.subscribe');
export const changesUnsubscribeMessageSchema = bareMessage('changes.unsubscribe');
export const changesSubscribedMessageSchema = bareMessage('changes.subscribed');
export const resourceChangedMessageSchema = z
  .object({ type: z.literal('resource.changed'), tenantId: tenantIdSchema, payload: resourceChangeSchema, ts: tsSchema })
  .strict();

/** Keepalive (PLAN §6.5: API Gateway idles out at 10 minutes; ping every 5). */
export const pingMessageSchema = bareMessage('ping');
export const pongMessageSchema = bareMessage('pong');

/** Every WS message, discriminated on `type`. */
export const wsMessageSchema = z.discriminatedUnion('type', [
  subscribeMessageSchema,
  subscribedMessageSchema,
  unsubscribeMessageSchema,
  rowPatchMessageSchema,
  rowStateMessageSchema,
  presenceUpdateMessageSchema,
  presenceStateMessageSchema,
  presenceLeaveMessageSchema,
  playoutSubscribeMessageSchema,
  playoutUnsubscribeMessageSchema,
  playoutStateMessageSchema,
  deviceStateMessageSchema,
  playoutLinkMessageSchema,
  changesSubscribeMessageSchema,
  changesUnsubscribeMessageSchema,
  changesSubscribedMessageSchema,
  resourceChangedMessageSchema,
  nackMessageSchema,
  pingMessageSchema,
  pongMessageSchema,
]);

/** What a server accepts from a client. */
export const clientMessageSchema = z.discriminatedUnion('type', [
  subscribeMessageSchema,
  unsubscribeMessageSchema,
  rowPatchMessageSchema,
  presenceUpdateMessageSchema,
  playoutSubscribeMessageSchema,
  playoutUnsubscribeMessageSchema,
  changesSubscribeMessageSchema,
  changesUnsubscribeMessageSchema,
  pingMessageSchema,
]);

/** What a client accepts from a server. */
export const serverMessageSchema = z.discriminatedUnion('type', [
  subscribedMessageSchema,
  rowPatchMessageSchema,
  rowStateMessageSchema,
  presenceStateMessageSchema,
  presenceLeaveMessageSchema,
  playoutStateMessageSchema,
  deviceStateMessageSchema,
  playoutLinkMessageSchema,
  changesSubscribedMessageSchema,
  resourceChangedMessageSchema,
  nackMessageSchema,
  pongMessageSchema,
]);

export type WsMessage = z.infer<typeof wsMessageSchema>;
export type WsMessageType = WsMessage['type'];
export type ClientMessage = z.infer<typeof clientMessageSchema>;
export type ServerMessage = z.infer<typeof serverMessageSchema>;
export type PresenceState = z.infer<typeof presenceStateSchema>;
export type PresenceMember = z.infer<typeof presenceMemberSchema>;

export type ParseResult<T> = { ok: true; message: T } | { ok: false; error: string };

function parseWith<T>(schema: z.ZodType<T>, raw: string, parsing: ContractParsing): ParseResult<T> {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch (err) {
    return { ok: false, error: `not JSON: ${err instanceof Error ? err.message : String(err)}` };
  }
  const result = parseContract(schema, json, parsing);
  if (!result.success) return { ok: false, error: z.prettifyError(result.error) };
  return { ok: true, message: result.data };
}

/** Parse any frame. Never throws: a bad frame is a nack, not a crash. */
export function parseWsMessage(raw: string): ParseResult<WsMessage> {
  return parseWith(wsMessageSchema, raw, 'strict');
}

/** Parse a frame a server received — always strictly: a server accepts only what the contract declares. A server-only type (`row.state`, …) from a client is refused. */
export function parseClientMessage(raw: string): ParseResult<ClientMessage> {
  return parseWith(clientMessageSchema, raw, 'strict');
}

/** Parse a frame a client received: `'tolerant'` in a running client (a newer server's added fields are dropped), `'strict'` in tests (D35). */
export function parseServerMessage(raw: string, parsing: ContractParsing): ParseResult<ServerMessage> {
  return parseWith(serverMessageSchema, raw, parsing);
}
